import type { BenefitDetailData } from "./useBenefitDetailData";
import { LoadingDetail, PlanAttributesFallback } from "./blocks/detailPrimitives";
import { MedicalBenefitBlock } from "./blocks/MedicalBenefitBlock";
import { WbBenefitsBlock, hasRawBenefits } from "./blocks/WbBenefitsBlock";
import { LifeDisabilityBenefitBlock } from "./blocks/LifeDisabilityBenefitBlock";
import { SupplementalBenefitBlock } from "./blocks/SupplementalBenefitBlock";
import { FsaBenefitBlock } from "./blocks/FsaBenefitBlock";

interface BenefitDetailRendererProps {
  // The plan record, used for the empty-state attribute fallback.
  plan: any;
  // The detail bundle for this plan, from useBenefitDetailData().get(planId).
  detailData: BenefitDetailData | undefined;
  // FSA only: the employee's elected annual amount (from the enrollment
  // selection, not the plan config).
  annualElection?: number | null;
}

/**
 * Dumb type-dispatcher: given the already-fetched detail bundle for a plan,
 * renders the matching per-type benefit block. Handles loading/empty centrally
 * before dispatch. No data fetching here — see useBenefitDetailData.
 */
export function BenefitDetailRenderer({
  plan,
  detailData,
  annualElection,
}: BenefitDetailRendererProps) {
  if (!detailData || detailData.status === "loading") return <LoadingDetail />;
  if (detailData.status === "empty") return <PlanAttributesFallback plan={plan} />;

  switch (detailData.kind) {
    case "medical": {
      // Raw W&B lines (the Plan Builder's editable source) take precedence so
      // every portal shows exactly what the library shows; parsed categories
      // remain the fallback for plans without raw lines.
      const raw = detailData.payload?.raw;
      if (hasRawBenefits(raw)) return <WbBenefitsBlock raw={raw} planType={plan?.type} />;
      return <MedicalBenefitBlock benefits={detailData.payload?.categories ?? detailData.payload} />;
    }
    case "lifeDisability": {
      // Structured coverage config first; template benefit lines (SBC-style
      // summary from the Plan Builder) beneath when present.
      const raw = detailData.payload?.raw;
      const config = detailData.payload?.config;
      return (
        <div className="space-y-3">
          {config && (
            <LifeDisabilityBenefitBlock
              config={config}
              ageReductions={detailData.payload?.ageReductions ?? []}
            />
          )}
          {hasRawBenefits(raw) && <WbBenefitsBlock raw={raw} planType={plan?.type} />}
          {!config && !hasRawBenefits(raw) && <PlanAttributesFallback plan={plan} />}
        </div>
      );
    }
    case "supplemental": {
      const raw = detailData.payload?.raw;
      const config = detailData.payload?.config;
      return (
        <div className="space-y-3">
          {config && <SupplementalBenefitBlock config={config} />}
          {hasRawBenefits(raw) && <WbBenefitsBlock raw={raw} planType={plan?.type} />}
          {!config && !hasRawBenefits(raw) && <PlanAttributesFallback plan={plan} />}
        </div>
      );
    }
    case "fsa":
      return <FsaBenefitBlock config={detailData.payload} annualElection={annualElection} />;
    default:
      return <PlanAttributesFallback plan={plan} />;
  }
}
